import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dataService } from '../../services/dataService';
import { StudentGroup } from '../../data/types';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Toggle from '../ui/Toggle';

const StudentGroupFormScreen: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const isEditMode = !!groupId;
  
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    status: 'active' | 'inactive';
  }>({
    name: '',
    description: '',
    status: 'active',
  });
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGroup = async () => {
      if (!isEditMode) return;
      
      try {
        setLoading(true);
        const group = await dataService.getStudentGroup(groupId);
        
        if (!group) {
          setError('Group not found');
          return;
        }
        
        setFormData({
          name: group.name,
          description: group.description,
          status: group.status,
        });
      } catch (error) {
        console.error('Error fetching group:', error);
        setError('Failed to load group data');
      } finally {
        setLoading(false);
      }
    };

    fetchGroup();
  }, [groupId, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleStatusChange = () => {
    setFormData(prev => ({
      ...prev,
      status: prev.status === 'active' ? 'inactive' : 'active'
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Group name is required');
      return;
    }
    
    try {
      setSaving(true);
      
      if (isEditMode && groupId) {
        // Update existing group
        const existingGroup = await dataService.getStudentGroup(groupId);
        
        if (!existingGroup) {
          setError('Group not found');
          return;
        }
        
        const updatedGroup: StudentGroup = {
          ...existingGroup,
          name: formData.name,
          description: formData.description,
          status: formData.status,
        };
        
        await dataService.updateStudentGroup(updatedGroup);
        navigate(`/student-groups/${groupId}`);
      } else {
        // Create new group
        const newGroup = await dataService.createStudentGroup({
          name: formData.name,
          description: formData.description,
          teacherId: 'teacher-001', // Mock teacher ID
          students: [],
          learningPaths: [],
        });
        
        navigate(`/student-groups/${newGroup.id}`);
      }
    } catch (error) {
      console.error('Error saving group:', error);
      setError('Failed to save group');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isEditMode && groupId) {
      navigate(`/student-groups/${groupId}`);
    } else {
      navigate('/student-groups');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? 'Edit Student Group' : 'Create Student Group'}
        </h1>
      </div>
      
      <Card>
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
              <p>{error}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Group Name*
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="Enter group name"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="Describe the purpose of this group"
                />
              </div>
              
              <div>
                <div className="flex items-center">
                  <Toggle
                    checked={formData.status === 'active'}
                    onChange={handleStatusChange}
                    label="Active"
                  />
                  <span className="ml-2 text-sm text-gray-500">
                    {formData.status === 'active' ? 'Group is active' : 'Group is inactive'}
                  </span>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    isEditMode ? 'Update Group' : 'Create Group'
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default StudentGroupFormScreen;
